import { useState, useEffect } from 'react'
import {
  Shield,
  Search,
  Filter,
  Download,
  Trash2,
  Calendar,
  User,
  Activity,
  AlertTriangle,
  Info,
  AlertOctagon,
  CheckCircle,
  ChevronLeft,
  ChevronRight
} from 'lucide-react'
import toast from 'react-hot-toast'
import api from '../services/api'

const SEVERITY_CONFIG = {
  info: { icon: Info, color: 'text-blue-600', bg: 'bg-blue-50', label: 'Info' },
  warning: { icon: AlertTriangle, color: 'text-yellow-600', bg: 'bg-yellow-50', label: 'Avertissement' },
  error: { icon: AlertOctagon, color: 'text-red-600', bg: 'bg-red-50', label: 'Erreur' },
  critical: { icon: AlertOctagon, color: 'text-red-900', bg: 'bg-red-100', label: 'Critique' }
}

const ACTION_LABELS = {
  'CREATE_USER': 'Création utilisateur',
  'UPDATE_USER': 'Modification utilisateur',
  'UPDATE_USER_ROLE': 'Modification rôle',
  'DELETE_USER': 'Suppression utilisateur',
  'LIST_USERS': 'Liste utilisateurs',
  'LOGIN': 'Connexion',
  'LOGOUT': 'Déconnexion',
  'SETUP_2FA': 'Activation 2FA',
  'VERIFY_2FA': 'Vérification 2FA',
  'DISABLE_2FA': 'Désactivation 2FA',
  'CREATE_PROJECT': 'Création projet',
  'UPDATE_PROJECT': 'Modification projet',
  'DELETE_PROJECT': 'Suppression projet',
  'IMPORT_RNCP': 'Import RNCP',
  'EXPORT_DATA': 'Export données',
  'PURGE_AUDIT_LOGS': 'Purge logs audit'
}

export default function Audit() {
  const [logs, setLogs] = useState([])
  const [users, setUsers] = useState([])
  const [stats, setStats] = useState(null)
  const [loading, setLoading] = useState(true)
  const [showFilters, setShowFilters] = useState(false)
  const [selectedLog, setSelectedLog] = useState(null)

  const [filters, setFilters] = useState({
    search: '',
    userId: '',
    action: '',
    entityType: '',
    severity: '',
    startDate: '',
    endDate: ''
  })

  const [pagination, setPagination] = useState({
    page: 1,
    limit: 50,
    total: 0,
    totalPages: 0
  })

  useEffect(() => {
    fetchLogs()
    fetchUsers()
    fetchStats()
  }, [filters, pagination.page])

  const fetchLogs = async () => {
    try {
      setLoading(true)
      const params = new URLSearchParams({
        page: pagination.page,
        limit: pagination.limit,
        ...Object.fromEntries(
          Object.entries(filters).filter(([_, v]) => v !== '')
        )
      })

      const response = await api.get(`/audit?${params}`)

      if (response.data.success) {
        setLogs(response.data.data.logs)
        setPagination(prev => ({
          ...prev,
          total: response.data.data.pagination.total,
          totalPages: response.data.data.pagination.totalPages
        }))
      }
    } catch (error) {
      console.error('[DEBUG Audit] Erreur lors du chargement des logs:', error)
      toast.error('Erreur lors du chargement des logs')
    } finally {
      setLoading(false)
    }
  }

  const fetchUsers = async () => {
    try {
      const response = await api.get('/audit/users')
      if (response.data.success) {
        setUsers(response.data.data)
      }
    } catch (error) {
      console.error('[DEBUG Audit] Erreur lors du chargement des utilisateurs:', error)
    }
  }

  const fetchStats = async () => {
    try {
      const params = new URLSearchParams()
      if (filters.startDate) params.append('startDate', filters.startDate)
      if (filters.endDate) params.append('endDate', filters.endDate)

      const response = await api.get(`/audit/stats?${params}`)
      if (response.data.success) {
        setStats(response.data.data)
      }
    } catch (error) {
      console.error('[DEBUG Audit] Erreur lors du chargement des statistiques:', error)
    }
  }

  const handleExport = async () => {
    try {
      const params = new URLSearchParams({
        ...Object.fromEntries(
          Object.entries(filters).filter(([_, v]) => v !== '')
        )
      })

      // Pour l'export, on récupère tous les logs (pas de pagination)
      const response = await api.get(`/audit?${params}&limit=10000`)

      if (response.data.success) {
        const csvContent = convertToCSV(response.data.data.logs)
        downloadCSV(csvContent, `audit_logs_${new Date().toISOString().split('T')[0]}.csv`)
        toast.success('Export réussi')
      }
    } catch (error) {
      console.error('[DEBUG Audit] Erreur lors de l\'export:', error)
      toast.error('Erreur lors de l\'export')
    }
  }

  const convertToCSV = (data) => {
    const headers = ['Date', 'Utilisateur', 'Action', 'Type d\'entité', 'Sévérité', 'IP', 'User Agent']
    const rows = data.map(log => [
      new Date(log.createdAt).toLocaleString('fr-FR'),
      log.user ? `${log.user.firstName} ${log.user.lastName}` : 'Système',
      ACTION_LABELS[log.action] || log.action,
      log.entityType || '-',
      SEVERITY_CONFIG[log.severity]?.label || log.severity,
      log.metadata?.ip || '-',
      log.metadata?.userAgent || '-'
    ])

    return [
      headers.join(','),
      ...rows.map(row => row.map(cell => `"${cell}"`).join(','))
    ].join('\n')
  }

  const downloadCSV = (content, filename) => {
    const blob = new Blob([content], { type: 'text/csv;charset=utf-8;' })
    const link = document.createElement('a')
    link.href = URL.createObjectURL(blob)
    link.download = filename
    link.click()
  }

  const handlePageChange = (newPage) => {
    setPagination(prev => ({ ...prev, page: newPage }))
  }

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    })
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center">
            <Shield className="h-8 w-8 mr-3 text-emerald-600" />
            Audit & Logs
          </h1>
          <p className="text-gray-500 mt-1">Traçabilité complète des actions sur la plateforme</p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setShowFilters(!showFilters)}
            className={`btn ${showFilters ? 'btn-primary' : 'btn-outline'}`}
          >
            <Filter className="h-4 w-4 mr-2" />
            Filtres
          </button>
          <button onClick={handleExport} className="btn btn-outline">
            <Download className="h-4 w-4 mr-2" />
            Exporter
          </button>
        </div>
      </div>

      {/* Statistiques */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="bg-white rounded-lg shadow p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Total logs</p>
                <p className="text-2xl font-bold text-gray-900">{stats.totalLogs}</p>
              </div>
              <Activity className="h-8 w-8 text-blue-500" />
            </div>
          </div>

          {stats.severityCounts.map(item => {
            const config = SEVERITY_CONFIG[item.severity]
            if (!config) return null
            const Icon = config.icon
            return (
              <div key={item.severity} className="bg-white rounded-lg shadow p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-500">{config.label}</p>
                    <p className="text-2xl font-bold text-gray-900">{item.count}</p>
                  </div>
                  <Icon className={`h-8 w-8 ${config.color}`} />
                </div>
              </div>
            )
          })}
        </div>
      )}

      {/* Filtres */}
      {showFilters && (
        <div className="bg-white rounded-lg shadow p-4 space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Recherche
              </label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                <input
                  type="text"
                  placeholder="Rechercher..."
                  value={filters.search}
                  onChange={(e) => setFilters(prev => ({ ...prev, search: e.target.value }))}
                  className="input pl-10 w-full"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Utilisateur
              </label>
              <select
                value={filters.userId}
                onChange={(e) => setFilters(prev => ({ ...prev, userId: e.target.value }))}
                className="input w-full"
              >
                <option value="">Tous les utilisateurs</option>
                {users.map(user => (
                  <option key={user.id} value={user.id}>
                    {user.firstName} {user.lastName}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Sévérité
              </label>
              <select
                value={filters.severity}
                onChange={(e) => setFilters(prev => ({ ...prev, severity: e.target.value }))}
                className="input w-full"
              >
                <option value="">Toutes les sévérités</option>
                <option value="info">Info</option>
                <option value="warning">Avertissement</option>
                <option value="error">Erreur</option>
                <option value="critical">Critique</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Date début
              </label>
              <input
                type="date"
                value={filters.startDate}
                onChange={(e) => setFilters(prev => ({ ...prev, startDate: e.target.value }))}
                className="input w-full"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Date fin
              </label>
              <input
                type="date"
                value={filters.endDate}
                onChange={(e) => setFilters(prev => ({ ...prev, endDate: e.target.value }))}
                className="input w-full"
              />
            </div>

            <div className="flex items-end">
              <button
                onClick={() => {
                  setFilters({
                    search: '',
                    userId: '',
                    action: '',
                    entityType: '',
                    severity: '',
                    startDate: '',
                    endDate: ''
                  })
                  setPagination(prev => ({ ...prev, page: 1 }))
                }}
                className="btn btn-outline w-full"
              >
                Réinitialiser
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Table des logs */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Date
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Utilisateur
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Action
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Type
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Sévérité
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {loading ? (
                <tr>
                  <td colSpan="6" className="px-6 py-12 text-center text-gray-500">
                    <div className="flex items-center justify-center">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-600"></div>
                      <span className="ml-3">Chargement...</span>
                    </div>
                  </td>
                </tr>
              ) : logs.length === 0 ? (
                <tr>
                  <td colSpan="6" className="px-6 py-12 text-center text-gray-500">
                    Aucun log trouvé
                  </td>
                </tr>
              ) : (
                logs.map(log => {
                  const severityConfig = SEVERITY_CONFIG[log.severity] || SEVERITY_CONFIG.info
                  const SeverityIcon = severityConfig.icon

                  return (
                    <tr key={log.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        <div className="flex items-center">
                          <Calendar className="h-4 w-4 mr-2 text-gray-400" />
                          {formatDate(log.createdAt)}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <User className="h-4 w-4 mr-2 text-gray-400" />
                          <div>
                            <div className="text-sm font-medium text-gray-900">
                              {log.user ? `${log.user.firstName} ${log.user.lastName}` : 'Système'}
                            </div>
                            {log.user && (
                              <div className="text-xs text-gray-500">{log.user.email}</div>
                            )}
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {ACTION_LABELS[log.action] || log.action}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {log.entityType || '-'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${severityConfig.bg} ${severityConfig.color}`}>
                          <SeverityIcon className="h-3 w-3 mr-1" />
                          {severityConfig.label}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        <button
                          onClick={() => setSelectedLog(log)}
                          className="text-emerald-600 hover:text-emerald-900"
                        >
                          Détails
                        </button>
                      </td>
                    </tr>
                  )
                })
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {pagination.totalPages > 1 && (
          <div className="bg-gray-50 px-6 py-4 flex items-center justify-between border-t border-gray-200">
            <div className="text-sm text-gray-700">
              Page {pagination.page} sur {pagination.totalPages} - {pagination.total} logs au total
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => handlePageChange(pagination.page - 1)}
                disabled={pagination.page === 1}
                className="btn btn-outline btn-sm"
              >
                <ChevronLeft className="h-4 w-4" />
              </button>
              <button
                onClick={() => handlePageChange(pagination.page + 1)}
                disabled={pagination.page === pagination.totalPages}
                className="btn btn-outline btn-sm"
              >
                <ChevronRight className="h-4 w-4" />
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Modal de détails */}
      {selectedLog && (
        <div className="fixed inset-0 bg-black bg-opacity-30 backdrop-blur-sm flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-medium text-gray-900">Détails du log</h3>
                <button
                  onClick={() => setSelectedLog(null)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  ×
                </button>
              </div>
            </div>

            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Date</label>
                <p className="text-sm text-gray-900">{formatDate(selectedLog.createdAt)}</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Utilisateur</label>
                <p className="text-sm text-gray-900">
                  {selectedLog.user
                    ? `${selectedLog.user.firstName} ${selectedLog.user.lastName} (${selectedLog.user.email})`
                    : 'Système'}
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Action</label>
                <p className="text-sm text-gray-900">{ACTION_LABELS[selectedLog.action] || selectedLog.action}</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Type d'entité</label>
                <p className="text-sm text-gray-900">{selectedLog.entityType || '-'}</p>
              </div>

              {selectedLog.entityId && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">ID entité</label>
                  <p className="text-sm text-gray-900 font-mono">{selectedLog.entityId}</p>
                </div>
              )}

              {selectedLog.oldValues && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Anciennes valeurs</label>
                  <pre className="text-xs text-gray-900 bg-gray-50 p-3 rounded overflow-x-auto">
                    {JSON.stringify(selectedLog.oldValues, null, 2)}
                  </pre>
                </div>
              )}

              {selectedLog.newValues && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Nouvelles valeurs</label>
                  <pre className="text-xs text-gray-900 bg-gray-50 p-3 rounded overflow-x-auto">
                    {JSON.stringify(selectedLog.newValues, null, 2)}
                  </pre>
                </div>
              )}

              {selectedLog.metadata && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Métadonnées</label>
                  <div className="space-y-2">
                    {selectedLog.metadata.ip && (
                      <p className="text-sm text-gray-900"><span className="font-medium">IP:</span> {selectedLog.metadata.ip}</p>
                    )}
                    {selectedLog.metadata.userAgent && (
                      <p className="text-sm text-gray-900"><span className="font-medium">User Agent:</span> {selectedLog.metadata.userAgent}</p>
                    )}
                  </div>
                </div>
              )}
            </div>

            <div className="p-6 border-t border-gray-200 flex justify-end">
              <button onClick={() => setSelectedLog(null)} className="btn btn-outline">
                Fermer
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
